// useCurrentShiftOrders.js
// Subscribes to the active shift and its orders. Returns derived buckets
// (drawing / cooking / queued / lastDone) plus the active shift doc.
//
// Used by every overlay that needs queue data: Main, StartingSoon, Reference,
// Break, Outro, FaceCam.
//
// Auth: handles its own anonymous sign-in so overlay routes can be plain
// browser sources with no login.
import { useEffect, useRef, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { onAuthStateChanged, signInAnonymously } from "firebase/auth";
import { auth, db } from "./../firebase";
import { resolveShift } from "./../utils/shiftScope";
import { DEFAULT_BOOTH_CODE } from "./../constants/booths";

const ACTIVE_STATES = [
  "queued",
  "drawing",
  "cooking",
  "flipping",
  "awaiting_photo",
  "suspended",
  "ready_to_deliver",
  "done",
];

export default function useCurrentShiftOrders() {
  const [authed, setAuthed] = useState(!!auth.currentUser);
  const [activeShift, setActiveShift] = useState(null);
  const [shiftLoaded, setShiftLoaded] = useState(false);
  const [concurrentCount, setConcurrentCount] = useState(0);
  const [orders, setOrders] = useState([]);

  // Per-order previous-state map for transition detection (cooking → flipping,
  // → ready_to_deliver). Consumers read this through the `transitions` field
  // on the snapshot return.
  const prevStatesRef = useRef({});
  const [transitions, setTransitions] = useState({
    flippedNow: false,
    flippedAt: null,       // Firestore Timestamp from the flipping order
    readyToDeliverNow: false,
  });

  useEffect(() => {
    // Wait for the persisted session to restore before going anonymous —
    // signInAnonymously at mount races the restore and replaces a signed-in
    // staff session for the whole browser profile.
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u || auth.currentUser) { setAuthed(true); return; }
      signInAnonymously(auth).catch(() => {});
    });
    return unsub;
  }, []);

  // Active shift listener.
  //
  // Scoped since 2026-09-25: this was limit(1) → docs[0], which meant every
  // OBS browser source independently coin-flipped between concurrent events.
  // Two rigs at two venues would drift onto each other's queues mid-scene.
  //
  // An overlay's scope comes from its URL (`/overlay/main?s=KXQD`) — the most
  // reliable binding available, because it is set once per rig and survives
  // cache clears, unlike localStorage. resolveShift still short-circuits to
  // the only active shift when there is just one, so existing browser-source
  // URLs with no ?s= keep working untouched on ordinary days.
  useEffect(() => {
    if (!authed) return;
    const q = query(collection(db, "shifts"), where("isActive", "==", true));
    return onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Ambiguous and unresolvable → null. An overlay showing NOTHING is a
      // visible, fixable problem; an overlay showing the wrong venue's queue
      // on a public screen is not.
      // defaultCode: a bare browser-source URL is the house rig. See
      // constants/booths.js — overlays only; staff and guests never get this.
      setActiveShift(resolveShift(all, { defaultCode: DEFAULT_BOOTH_CODE }).shift);
      // How many queues are live right now. Exposed so guest-facing surfaces
      // can show the booth code ONLY when it disambiguates something — no new
      // Firestore listener, this snapshot is already here.
      setConcurrentCount(all.length);
      setShiftLoaded(true);
    });
  }, [authed]);

  // Orders listener — depends on active shift id
  useEffect(() => {
    if (!activeShift) {
      setOrders([]);
      prevStatesRef.current = {};
      return;
    }
    const q = query(
      collection(db, "orders"),
      where("shiftId", "==", activeShift.id),
      where("state", "in", ACTIVE_STATES)
    );
    return onSnapshot(q, (snap) => {
      const next = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((o) => !o.trashedAt)
        .sort((a, b) => (a.queueNumber ?? 0) - (b.queueNumber ?? 0));

      // Detect transitions before swapping the prev-state map
      const flippedNow = next.some(
        (o) => prevStatesRef.current[o.id] === "cooking" && o.state === "flipping"
      );
      const readyToDeliverNow = next.some(
        (o) =>
          prevStatesRef.current[o.id] !== undefined &&
          prevStatesRef.current[o.id] !== "ready_to_deliver" &&
          o.state === "ready_to_deliver"
      );

      const newStates = {};
      next.forEach((o) => { newStates[o.id] = o.state; });
      prevStatesRef.current = newStates;

      setOrders(next);
      if (flippedNow || readyToDeliverNow) {
        // Grab the flippedAt timestamp from the order that just transitioned
        const flippedOrder = flippedNow
          ? next.find((o) => o.state === "flipping")
          : null;
        setTransitions({
          flippedNow,
          flippedAt: flippedOrder?.flippedAt ?? null,
          readyToDeliverNow,
        });
      }
    });
  }, [activeShift?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derived buckets — recomputed on every orders change
  const drawing = orders.find((o) => o.state === "drawing") ?? null;
  const cookingAll = orders.filter((o) => o.state === "cooking" || o.state === "flipping");
  const cooking = cookingAll[0] ?? null;
  const queued = orders.filter(
    (o) => (o.state === "queued" || o.state === "suspended") && o.queue !== "standby"
  );
  const allActive = orders.filter((o) =>
    ["queued", "suspended", "drawing", "cooking", "flipping"].includes(o.state) &&
    o.queue !== "standby"
  );
  const recentlyDone = orders
    .filter(
      (o) =>
        (o.state === "ready_to_deliver" || o.state === "done") &&
        (o.photo_cutout_url || o.photo_url || o.cutoutThumbnailUrl || o.thumbnailUrl)
    )
    .sort(
      (a, b) =>
        (b.readyToDeliverAt?.toMillis?.() ?? b.deliveredAt?.toMillis?.() ?? 0) -
        (a.readyToDeliverAt?.toMillis?.() ?? a.deliveredAt?.toMillis?.() ?? 0)
    );

  // Consumer can call this to acknowledge a transition flag (one-shot)
  const clearTransition = (key) =>
    setTransitions((t) => ({ ...t, [key]: false }));

  return {
    activeShift,
    shiftLoaded,
    concurrentCount,
    orders,
    drawing,
    cooking,
    cookingAll,
    queued,
    allActive,
    recentlyDone,
    lastDone: recentlyDone[0] ?? null,
    transitions,
    clearTransition,
  };
}
